import { injectable } from 'tsyringe'
import ApiService from '@/app/infrastructures/services/ApiService'
import { DropBoxRepositoryInterface } from '@/data/persistences/repositories/contracts/DropBoxRepositoryInterface'
import { DropBoxMapper } from '@/data/persistences/mappers/DropBoxMapper'
import { DropBox, DropBoxes } from '@/domain/entities/DropBox'
import { Endpoints } from '@/app/infrastructures/misc/Endpoints'
import {
  CreateDropBoxRequestInterface,
  UpdateDropBoxRequestInterface,
} from '@/data/payload/contracts/DropBoxRequest'
import { DeleteDropBoxRequest } from '@/data/payload/api/DropBoxRequest'

@injectable()
export class DropBoxApiRepository implements DropBoxRepositoryInterface {
  private service: ApiService
  private mapper: DropBoxMapper

  constructor(service: ApiService, mapper: DropBoxMapper) {
    this.service = service
    this.mapper = mapper
  }

  public async getAll(
    params: Map<string, string | number | boolean>
  ): Promise<DropBoxes> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.DROP_BOX,
      // Workaround: ApiService uses qs.stringify which doesn't support Map.
      // We pass a plain object cast as any to ensure query params are serialized.
      Object.fromEntries(params) as any
    )
    return this.mapper.convertDropBoxesFromApi(resp)
  }

  public async getMonitoring(
    params: Map<string, string | number | boolean>
  ): Promise<DropBoxes> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.DROP_BOX,
      Object.fromEntries(params) as any
    )
    return this.mapper.convertDropBoxesFromApi(resp)
  }

  public async get(externalId: string): Promise<DropBox | null> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.DROP_BOX,
      // Workaround: Pass as plain object cast to any for proper serialization
      { external_id: externalId } as any
    )
    return this.mapper.convertDropBoxDetailFromApi(resp)
  }

  public async create(
    payload: CreateDropBoxRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.DROP_BOX,
      undefined,
      payload as CreateDropBoxRequestInterface,
      new Map([['Content-Type', 'multipart/form-data;']])
    )
    return true
  }

  public async update(
    payload: UpdateDropBoxRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.DROP_BOX,
      undefined,
      payload as UpdateDropBoxRequestInterface,
      new Map([['Content-Type', 'multipart/form-data;']])
    )
    return true
  }

  public async delete(externalId: string): Promise<boolean> {
    const payload = new DeleteDropBoxRequest(externalId)
    await this.service.invoke(
      'delete',
      Endpoints.Route.DROP_BOX,
      undefined,
      payload
    )
    return true
  }
}
